import { ButtonBase, Stack, Typography } from '@mui/material';
import _ from 'lodash';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

export const getTaskInitials = (firstName: string | undefined, lastName: string | undefined, fallback: string | undefined) => {
  const initials = `${_.head(firstName) ?? ''}${_.head(lastName) ?? ''}`.toUpperCase();
  return !_.isEmpty(initials) ? initials : (_.head(fallback) ?? '?').toUpperCase();
};

interface SectionProps {
  icon: React.ReactNode;
  iconBackground: string;
  iconColor: string;
  title: string;
  count: number | undefined;
  hint?: string;
  children: React.ReactNode;
}

export const TmSectionCard: React.FC<SectionProps> = ({ icon, iconBackground, iconColor, title, count, hint, children }) => {
  const { classes } = useStyles();

  return (
    <div className={classes.card}>
      <Stack className={classes.cardHeader} direction={'row'} alignItems={'center'} gap={1.5}>
        <span className={classes.iconTile} style={{ background: iconBackground, color: iconColor }}>
          {icon}
        </span>
        <Typography className={classes.cardTitle}>{title}</Typography>
        <div className={classes.count}>{count ?? 0}</div>
        {!_.isEmpty(hint) ? <Typography className={classes.hint}>{hint}</Typography> : null}
      </Stack>
      <div>{children}</div>
    </div>
  );
};

interface RowProps {
  initials: string;
  name: string;
  meta: string;
  context: string;
  contextLabel: string;
  amount?: string;
  actionText: string;
  disabled?: boolean;

  handleAction(): void;
}

export const TmTaskRow: React.FC<RowProps> = ({ initials, name, meta, context, contextLabel, amount, actionText, disabled, handleAction }) => {
  const { classes } = useStyles();

  return (
    <div
      className={classes.row}
      role={!disabled ? 'button' : undefined}
      tabIndex={!disabled ? 0 : undefined}
      onClick={!disabled ? handleAction : undefined}
      onKeyDown={
        !disabled
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleAction();
              }
            }
          : undefined
      }
    >
      <span className={classes.avatar}>{initials}</span>
      <div className={classes.identity}>
        <Typography className={classes.name}>{name}</Typography>
        <Typography className={classes.meta}>{meta}</Typography>
      </div>
      <div className={classes.context}>
        <Typography className={classes.contextText}>{context}</Typography>
        <Typography className={classes.meta}>{contextLabel}</Typography>
      </div>
      <span className={classes.amount}>{amount}</span>
      <ButtonBase
        className={classes.action}
        disabled={disabled}
        onClick={(event) => {
          event.stopPropagation();
          handleAction();
        }}
      >
        {actionText}
      </ButtonBase>
    </div>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  cardHeader: {
    paddingBottom: '12px',
    borderBottom: `1px solid ${theme.palette.greys.lighter}`,
  },
  iconTile: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 'bold',
  },
  cardTitle: {
    fontSize: theme.typography.pxToRem(17),
    fontWeight: 500,
  },
  count: {
    fontSize: theme.typography.pxToRem(12),
    background: theme.palette.text.primary,
    color: '#FFF',
    borderRadius: '4px',
    lineHeight: 1,
    height: 'fit-content',
    width: 'fit-content',
    padding: '4px 6px',
  },
  hint: {
    marginLeft: 'auto',
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '14px 0',
    borderBottom: '1px solid #F0F0F5',
    cursor: 'pointer',
    '&:hover': {
      background: '#FAFAFC',
    },
    '&:last-of-type': {
      borderBottom: 'none',
    },
  },
  avatar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    background: theme.palette.backgrounds.main,
    color: theme.palette.primary.main,
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 'bold',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  identity: {
    width: '260px',
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  name: {
    fontSize: theme.typography.pxToRem(15),
    fontWeight: 500,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  meta: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  context: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  contextText: {
    fontSize: theme.typography.pxToRem(15),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  amount: {
    minWidth: '44px',
    textAlign: 'right',
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
  action: {
    height: '36px',
    padding: '0 18px',
    borderRadius: '8px',
    background: 'linear-gradient(to right, #4F44FF, #007FFF)',
    color: '#FFF',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 'bold',
    flexShrink: 0,
    whiteSpace: 'nowrap',
    '&:hover': {
      background: 'linear-gradient(to right, #007FFF, #4F44FF)',
    },
    '&.Mui-disabled': {
      opacity: 0.5,
      color: '#FFF',
    },
  },
}));
